/**
 * useSignatureExport — the on-device signature retention safety net for Manual
 * Checkout, in one place: the local count, best-effort persist-on-checkout, and
 * the export-to-`{docNum}.png` action.
 *
 * (2026-08-03) Extracted from ManualCheckout.tsx so that page stays focused and
 * the signature-recovery logic is reusable and testable in isolation. See
 * utils/signatureStore.ts (durable IndexedDB store) and utils/exportSignatures.ts
 * (export/download). Message/UI wording stays with the page; this hook owns only
 * the state and the store/export plumbing.
 */
import { useCallback, useEffect, useState } from 'react';
import { signatureStore, mapByDocNum, DEFAULT_MAX_SIGNATURES } from '../utils/signatureStore';
import { exportStoredSignatures, ExportResult } from '../utils/exportSignatures';
import { configService } from '../services/configService';

/** A checkout result row a stored signature is keyed by (one submit → one image). */
export interface ProcessedTicket {
  docNum: string;
  rfid: string;
}

export interface UseSignatureExport {
  /** How many signatures are retained on this device. */
  storedSigCount: number;
  /**
   * docNum → signature data URL for everything retained on this device, so
   * History can display a signature the server never managed to store. Empty
   * when IndexedDB is unavailable or nothing has been captured here.
   */
  localSignatures: Map<string, string>;
  /** True while an export is in flight (drives the button's disabled state). */
  exportingSigs: boolean;
  /** Re-read the on-device count (and the docNum → image lookup). */
  refreshStoredSigCount: () => Promise<void>;
  /**
   * Best-effort: durably retain the drawn signature keyed by the ticket
   * docNum(s) the backend just assigned, so it survives a failed server-side
   * S3 upload. Never throws — a storage failure must not affect the checkout.
   * A no-op when there's no signature or nothing was processed.
   */
  persistSignature: (signature: string | null, processed: ProcessedTicket[]) => Promise<void>;
  /** Export all retained signatures; toggles `exportingSigs` and returns the result. */
  runExport: () => Promise<ExportResult>;
}

export function useSignatureExport(): UseSignatureExport {
  const [storedSigCount, setStoredSigCount] = useState(0);
  const [localSignatures, setLocalSignatures] = useState<Map<string, string>>(() => new Map());
  const [exportingSigs, setExportingSigs] = useState(false);

  const refreshStoredSigCount = useCallback(async () => {
    try {
      const all = await signatureStore.getAll();
      setStoredSigCount(all.length);
      // Same read serves both the count and History's docNum → image lookup, so
      // displaying local signatures costs no extra IndexedDB round-trip.
      setLocalSignatures(mapByDocNum(all));
    } catch {
      // IndexedDB unavailable (private mode / unsupported) — leave count at 0.
    }
  }, []);

  // Load the count once on mount so the export button can show it (and disable
  // when there's nothing to export).
  useEffect(() => {
    refreshStoredSigCount();
  }, [refreshStoredSigCount]);

  const persistSignature = useCallback(
    async (signature: string | null, processed: ProcessedTicket[]) => {
      if (!signature || processed.length === 0) return;
      try {
        let maxCount = DEFAULT_MAX_SIGNATURES;
        try {
          maxCount = configService.getConfig().features.maxStoredSignatures ?? DEFAULT_MAX_SIGNATURES;
        } catch {
          // Config not loaded yet — fall back to the default cap.
        }
        await signatureStore.saveSignature(
          {
            docNums: processed.map((p) => p.docNum),
            rfids: processed.map((p) => p.rfid),
            signature,
          },
          maxCount,
        );
        refreshStoredSigCount();
      } catch (persistErr) {
        console.error('Failed to persist signature locally:', persistErr);
      }
    },
    [refreshStoredSigCount],
  );

  const runExport = useCallback(async (): Promise<ExportResult> => {
    setExportingSigs(true);
    try {
      return await exportStoredSignatures();
    } finally {
      setExportingSigs(false);
    }
  }, []);

  return {
    storedSigCount,
    localSignatures,
    exportingSigs,
    refreshStoredSigCount,
    persistSignature,
    runExport,
  };
}
